import React, { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Dedicated axios instance for the member portal (separate token store from admin).
export const memberApi = axios.create({ baseURL: API_BASE });

memberApi.interceptors.request.use((config) => {
  // Prefer the member token; fall back to admin token so admins can preview /portal/* as themselves.
  const token = localStorage.getItem("epas_member_token") || localStorage.getItem("epas_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

memberApi.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401 && typeof window !== "undefined") {
      const path = window.location.pathname;
      const hasAdminToken = !!localStorage.getItem("epas_token");
      if (path.startsWith("/portal") && !["/portal/login", "/portal/forgot-password", "/portal/set-password"].includes(path)) {
        // Admin browsing /portal/* — send them back to admin, don't force portal login
        if (hasAdminToken) {
          window.location.href = "/admin";
          return Promise.reject(err);
        }
        localStorage.removeItem("epas_member_token");
        localStorage.removeItem("epas_member_email");
        window.location.href = "/portal/login";
      }
    }
    return Promise.reject(err);
  }
);

const MemberAuthContext = createContext(null);

export const MemberAuthProvider = ({ children }) => {
  const [member, setMember] = useState(null);
  const [booted, setBooted] = useState(false);

  useEffect(() => {
    const memberToken = localStorage.getItem("epas_member_token");
    const adminToken = localStorage.getItem("epas_token");
    const token = memberToken || adminToken;
    if (token) {
      memberApi.get("/member/auth/me")
        .then(({ data }) => setMember(data.member))
        .catch(() => {
          // If ONLY the member token failed, clear it. Never clear the admin token here.
          if (memberToken && !adminToken) {
            localStorage.removeItem("epas_member_token");
            localStorage.removeItem("epas_member_email");
          }
        })
        .finally(() => setBooted(true));
    } else {
      setBooted(true);
    }
  }, []);

  const login = async (email, password) => {
    const { data } = await memberApi.post("/member/auth/login", { email, password });
    localStorage.setItem("epas_member_token", data.token);
    localStorage.setItem("epas_member_email", data.email);
    setMember(data.member);
    return data;
  };

  const setPasswordWithToken = async (token, password) => {
    const { data } = await memberApi.post("/member/auth/set-password", { token, password });
    localStorage.setItem("epas_member_token", data.token);
    localStorage.setItem("epas_member_email", data.email);
    setMember(data.member);
    return data;
  };

  const logout = () => {
    localStorage.removeItem("epas_member_token");
    localStorage.removeItem("epas_member_email");
    setMember(null);
  };

  return (
    <MemberAuthContext.Provider value={{ member, login, logout, setPasswordWithToken, booted, setMember }}>
      {children}
    </MemberAuthContext.Provider>
  );
};

export const useMemberAuth = () => useContext(MemberAuthContext);
